import React, { useState } from 'react';
import {
  IAuditoriaPejotizacaoResultado,
  IItemPejotizacao,
} from '../interfaces/IAuditoriaPejotizacao';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaPejotizacaoResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-291: drill-down a nível de lançamento — todo AVISO/ERRO precisa mostrar não só o valor
// total recorrente, mas os pagamentos individuais que formam o padrão de recorrência.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemPejotizacao }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Valor</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{formatCurrency(lanc.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaPejotizacaoDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const recorrentes = resultado.fornecedoresRecorrentes || [];
  const conciliados = resultado.fornecedoresConciliados || [];

  const erros = recorrentes.filter((item) => item.status === 'ERRO');
  const avisos = recorrentes.filter((item) => item.status === 'AVISO');

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  const renderTabela = (titulo: string, itens: IItemPejotizacao[], corLinha: string, mostrarPenalidades: boolean) => (
    <div className='auditoria-card'>
      <h3 className='mb-0'>{titulo}</h3>

      {itens.length === 0 && (
        <div className='text-muted mt-4'>Nenhuma ocorrência identificada no período.</div>
      )}

      {itens.length > 0 && (
        <div className='table-responsive mt-4'>
          <table className='auditoria-table'>
            <thead>
              <tr>
                <th>Fornecedor</th>
                <th>Conta</th>
                <th>Frequência de Repetição</th>
                <th className='text-end'>Valor Total</th>
                <th className='text-center'>Sócio (QSA)</th>
                <th className='text-center'>Status</th>
                {mostrarPenalidades && <th className='text-end'>Impacto Fiscal Total</th>}
              </tr>
            </thead>
            <tbody>
              {itens.map((item, idx) => {
                const chave = `pejo-${item.empresaId}-${item.contaContabil}-${item.fornecedorIdentificado}-${idx}`;
                const expandido = itemExpandido === chave;

                return (
                  <React.Fragment key={chave}>
                    <tr className={`auditoria-row-tributo ${corLinha}`} onClick={() => alternarExpandir(chave)}>
                      <td>
                        <strong>{item.fornecedorIdentificado}</strong>
                        <div className='text-muted' style={{ fontSize: 11 }}>
                          {formatDate(item.primeiroPagamento)} a {formatDate(item.ultimoPagamento)}
                        </div>
                      </td>
                      <td>
                        <div>{item.descricaoConta || item.contaContabil}</div>
                        <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial || item.contaContabil}</div>
                      </td>
                      <td>{item.frequenciaRepeticao}</td>
                      <td className='text-end'>{formatCurrency(item.valorTotalPeriodo)}</td>
                      <td className='text-center'>
                        {item.ehSocio ? (
                          <span className='auditoria-status status-ERRO' style={{ fontSize: 10 }}>
                            {item.nomeSocioCorrespondente || 'SIM'}
                          </span>
                        ) : (
                          <span className='text-muted'>Não confirmado</span>
                        )}
                      </td>
                      <td className='text-center'>
                        <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                        <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                      </td>
                      {mostrarPenalidades && <td className='text-end auditoria-col-highlight'>{formatCurrency(item.impactoFiscalTotal)}</td>}
                    </tr>

                    {expandido && (
                      <tr>
                        <td colSpan={mostrarPenalidades ? 7 : 6} style={{ background: item.status === 'ERRO' ? '#fff5f5' : '#fffbeb' }}>
                          <div className='py-2 px-3'>
                            <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                            {item.memoriaCalculo && <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>}
                            <BlocoLancamentosRelacionados item={item} />
                            {item.ehSocio && (
                              <div className='mt-2'>
                                <strong>Composição da penalidade:</strong>
                                <ul className='mb-0'>
                                  <li>Recomposição IRPJ/CSLL (34%): {formatCurrency(item.recomposicaoIrpjCsll)}</li>
                                  <li>Encargos Laborais (58,5%): {formatCurrency(item.encargosLaborais)}</li>
                                  <li>Multa Sonegação (150%) + Multa ECD (5%, lim. 1% RB): {formatCurrency(item.multaSonegacaoEcd)}</li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Governança e Detecção de Pejotização — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Risco Fiscal (Sócio Confirmado)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>34% Recomposição + 58,5% Encargos + 150% Sonegação + Multa ECD (5%, lim. 1% RB).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Valor Pago a Fornecedores Recorrentes</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          <div className='auditoria-kpi-help'>Soma dos valores pagos a fornecedores com padrão de recorrência (Erro + Aviso).</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Consulta ao QSA</div>
          <div className='auditoria-kpi-value'>
            <span className={`auditoria-status ${resultado.qsaDisponivel ? 'status-CONCILIADO' : 'status-AVISO'}`}>
              {resultado.qsaDisponivel ? 'Disponível' : 'Indisponível'}
            </span>
          </div>
          <div className='auditoria-kpi-help'>{resultado.mensagemQsa || 'Quadro de Sócios e Administradores consultado via fonte pública de CNPJ.'}</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada à detecção de pejotização.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div>{resultado.fundamentacaoLegal}</div>
        </div>
      )}

      {renderTabela('Erro — Pejotização Confirmada (Sócio no QSA)', erros, 'status-erro', true)}
      {renderTabela('Aviso — Padrão de Recorrência (Sócio Não Confirmado)', avisos, 'status-aviso', false)}
      {renderTabela('Fornecedores Conciliados (Sem Padrão de Recorrência)', conciliados, '', false)}
    </>
  );
};

export default AuditoriaPejotizacaoDetalhe;
